import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import orangeSortIcon from "./../../media/orange-sort-icon.svg";
import "./../../styleSheet/main/admin/addMaritexContact/addMaritexContact.css";
import Logger from "../../utils/Logger";
import { useNotification } from "../../components/NotificationContext";
import httpClient from "../../services/httpClient.js";
import Stars from "./../../media/Stars.svg";

function AddMaritexContact() {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [internalPhoneNumber, setInternalPhoneNumber] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [mobilePhoneNumber, setMobilePhoneNumber] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [departmentId, setDepartmentId] = useState("");
  const [roleId, setRoleId] = useState("");
  const [roleInDepartment, setRoleInDepartment] = useState("");
  const [departments, setDepartments] = useState([]); // Lista działów
  const [roles, setRoles] = useState([]); // Lista ról
  const [successMessage, setSuccessMessage] = useState("");
  const [hover, setHover] = useState(false);
  const [isLoading, setIsLoading] = useState(false); // Dodane loading state
  const navigate = useNavigate();
  const { addNotification } = useNotification();
  const { t } = useTranslation();

  // Funkcja do generowania hasła
  const generatePassword = () => {
    const length = 12;
    const charset =
      "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*";
    let generatedPassword = "";

    // Zapewnienie, że hasło zawiera przynajmniej jedną wielką literę, małą literę, cyfrę i znak specjalny
    const lowercase = "abcdefghijklmnopqrstuvwxyz";
    const uppercase = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const numbers = "0123456789";
    const symbols = "!@#$%^&*";

    // Dodanie po jednym znaku z każdej kategorii
    generatedPassword += lowercase.charAt(
      Math.floor(Math.random() * lowercase.length),
    );
    generatedPassword += uppercase.charAt(
      Math.floor(Math.random() * uppercase.length),
    );
    generatedPassword += numbers.charAt(
      Math.floor(Math.random() * numbers.length),
    );
    generatedPassword += symbols.charAt(
      Math.floor(Math.random() * symbols.length),
    );

    // Uzupełnienie reszty hasła losowymi znakami
    for (let i = 4; i < length; i++) {
      generatedPassword += charset.charAt(
        Math.floor(Math.random() * charset.length),
      );
    }

    // Przetasowanie znaków w haśle
    generatedPassword = generatedPassword
      .split("")
      .sort(() => Math.random() - 0.5)
      .join("");

    setPassword(generatedPassword);
    // showNotification("Hasło zostało wygenerowane!", "success");
  };

  // Pobieranie działów z API
  const fetchDepartments = async () => {
    try {
      const response = await httpClient.get("Departments", {
        withAuth: true,
      });
      // Sortowanie działów alfabetycznie po nazwie
      const sortedDepartments = response.data.sort((a, b) =>
        (a?.name || "").localeCompare(b?.name || "", "pl", {
          sensitivity: "base",
        }),
      );
      setDepartments(sortedDepartments);
    } catch (error) {
      Logger.error("Błąd podczas pobierania działów:", error);
      // showNotification("Błąd podczas pobierania działów", "error");
    }
  };

  // Pobieranie ról z API
  const fetchRoles = async () => {
    try {
      const response = await httpClient.get("Users/available-roles", {
        withAuth: true,
      });
      Logger.log("Roles data structure:", response.data);
      setRoles(response.data);
    } catch (error) {
      Logger.error("Błąd podczas pobierania ról:", error);
      // showNotification("Błąd podczas pobierania ról", "error");
    }
  };

  useEffect(() => {
    fetchDepartments();
    fetchRoles();
  }, []);

  // Wysyłanie danych do API
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !firstName ||
      !lastName ||
      !email ||
      !password ||
      !departmentId ||
      !roleId ||
      !roleInDepartment
    ) {
      addNotification(t("contactDatabase.requiredFields"), "error");
      return;
    }

    setIsLoading(true); // Rozpocznij loading

    const userData = {
      firstName,
      lastName,
      email,
      role: roleId,
      password,
      departmentId,
      company: "Niviss",
      roleInDepartment,
      ...(phoneNumber && phoneNumber.trim() !== "" && { phoneNumber }),
      ...(mobilePhoneNumber &&
        mobilePhoneNumber.trim() !== "" && { mobilePhoneNumber }),
      ...(internalPhoneNumber &&
        internalPhoneNumber.trim() !== "" && { internalPhoneNumber }),
    };

    Logger.log(userData);

    try {
      const response = await httpClient.post("Users", userData, {
        withAuth: true,
      });

      setTimeout(() => {
        navigate("/administrator/baza-teleadresowa/niviss/");
      }, 1000);

      if (response.status === 201) {
        setSuccessMessage(t("contactDatabase.employeeAdded"));
        addNotification(t("contactDatabase.employeeAdded"), "success");
        setFirstName("");
        setLastName("");
        setInternalPhoneNumber("");
        setPhoneNumber("");
        setMobilePhoneNumber("");
        setEmail("");
        setPassword("");
        setDepartmentId("");
        setRoleId("");
        setRoleInDepartment("");
      } else {
        // showNotification(
        //   "Wystąpił błąd podczas dodawania pracownika.",
        //   "error"
        // );
      }
    } catch (error) {
      const backendErrors = error?.response?.data;
      const errorDetails = Array.isArray(backendErrors)
        ? backendErrors
            .map((item) => item?.message || item)
            .filter(Boolean)
            .join(" | ")
        : backendErrors?.message || "";

      Logger.error("Błąd podczas wysyłania danych:", error);
      addNotification(
        errorDetails
          ? t("contactDatabase.errorAddingEmployeeWithDetails", {
              details: errorDetails,
            })
          : t("contactDatabase.errorAddingEmployee"),
        "error",
      );
    } finally {
      setIsLoading(false); // Zakończ loading
    }
  };

  return (
    <div className="add-announcement-container">
      <div className="main-site">
        <h1>{t("contactDatabase.addNewEmployeeTitle")}</h1>
        <div
          className="orange-sort-icon"
          onClick={() => navigate("/administrator/baza-teleadresowa/niviss")}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          style={{
            backgroundImage: `url(${orangeSortIcon})`,
            transform: hover ? "rotate(180deg)" : "rotate(90deg)",
            cursor: "pointer",
          }}
        />
      </div>
      <form onSubmit={handleSubmit} className="add-announcement-form">
        <div className="form-columns">
          <div className="left-column">
            <div className="form-group">
              <label htmlFor="firstName">
                {t("contactDatabase.firstName")}:
              </label>
              <input
                type="text"
                id="firstName"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder={t("contactDatabase.enterFirstName")}
                disabled={isLoading}
              />
            </div>
            <div className="form-group">
              <label htmlFor="lastName">{t("contactDatabase.lastName")}:</label>
              <input
                type="text"
                id="lastName"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder={t("contactDatabase.enterLastName")}
                disabled={isLoading}
              />
            </div>
            <div className="form-group">
              <label htmlFor="email">{t("contactDatabase.email")}:</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("contactDatabase.enterEmail")}
                disabled={isLoading}
              />
            </div>
            <div className="form-group">
              <label htmlFor="password">{t("contactDatabase.password")}:</label>
              <div
                style={{
                  position: "relative",
                  border: "1px solid white",
                }}
              >
                <input
                  type="text"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t("contactDatabase.enterPassword")}
                  style={{
                    paddingRight: "40px",
                    border: "none",
                    background: "transparent",
                    width: "100%",
                  }}
                  disabled={isLoading}
                />
                <img
                  src={Stars}
                  alt="Stars"
                  style={{
                    position: "absolute",
                    right: "18px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    width: "24px",
                    height: "24px",
                    cursor: isLoading ? "not-allowed" : "pointer",
                    opacity: isLoading ? 0.5 : 1,
                  }}
                  onClick={isLoading ? undefined : generatePassword}
                />
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="roleId">{t("contactDatabase.role")}:</label>
              <select
                id="roleId"
                value={roleId}
                onChange={(e) => setRoleId(e.target.value)}
                disabled={isLoading}
              >
                <option value="">{t("contactDatabase.selectRole")}</option>
                {roles.map((role, index) => (
                  <option key={`role-${index}`} value={role}>
                    {role}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="right-column">
            <div className="form-group">
              <label htmlFor="internalPhoneNumber">
                {t("contactDatabase.internalPhone")}:
              </label>
              <input
                type="text"
                id="internalPhoneNumber"
                value={internalPhoneNumber}
                onChange={(e) => setInternalPhoneNumber(e.target.value)}
                placeholder={t("contactDatabase.enterInternalPhone")}
                disabled={isLoading}
              />
            </div>
            <div className="form-group">
              <label htmlFor="phoneNumber">{t("contactDatabase.phone")}:</label>
              <input
                type="text"
                id="phoneNumber"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder={t("contactDatabase.enterPhone")}
                disabled={isLoading}
              />
            </div>
            <div className="form-group">
              <label htmlFor="mobilePhoneNumber">
                {t("contactDatabase.mobilePhone")}:
              </label>
              <input
                type="text"
                id="mobilePhoneNumber"
                value={mobilePhoneNumber}
                onChange={(e) => setMobilePhoneNumber(e.target.value)}
                placeholder={t("contactDatabase.enterMobilePhone")}
                disabled={isLoading}
              />
            </div>
            <div className="form-group">
              <label htmlFor="departmentId">
                {t("contactDatabase.department")}:
              </label>
              <select
                id="departmentId"
                value={departmentId}
                onChange={(e) => setDepartmentId(e.target.value)}
                disabled={isLoading}
              >
                <option value="">
                  {t("contactDatabase.selectDepartment")}
                </option>
                {departments.map((dept, index) => (
                  <option
                    key={dept?.id || `dept-${index}`}
                    value={dept?.id || ""}
                  >
                    {dept?.name || t("contactDatabase.unknownDepartment")}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="roleInDepartment">
                {t("contactDatabase.roleInDepartment")}:
              </label>
              <select
                id="roleInDepartment"
                value={roleInDepartment}
                onChange={(e) => setRoleInDepartment(e.target.value)}
                disabled={isLoading}
              >
                <option value="">
                  {t("contactDatabase.selectRoleInDepartment")}
                </option>
                <option value="Student">{t("roles.student")}</option>
                <option value="Employee">{t("roles.employee")}</option>
                <option value="DeputyManager">
                  {t("roles.deputyManager")}
                </option>
                <option value="Manager">{t("roles.manager")}</option>
              </select>
            </div>
          </div>
        </div>
        <div className="button-employee">
          {/* Loading indicator */}
          {isLoading && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginRight: "15px",
                marginBottom: "10px", // Dodane - przesuwa w dół
                marginLeft: "-20px", // Dodane - przesuwa bardziej w lewo
                color: "#ff9900",
              }}
            >
              <div
                style={{
                  width: "20px",
                  height: "20px",
                  border: "2px solid #ff9900",
                  borderTop: "2px solid transparent",
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite",
                  marginRight: "8px",
                }}
              ></div>
              <span>{t("contactDatabase.addingEmployee")}</span>
            </div>
          )}
          <button
            type="submit"
            className="submit-button-employee"
            disabled={isLoading}
            style={{
              opacity: isLoading ? 0.6 : 1,
              cursor: isLoading ? "not-allowed" : "pointer",
            }}
          >
            {isLoading
              ? t("common.adding")
              : t("contactDatabase.addEmployeeButton")}
          </button>
        </div>
      </form>
      {successMessage && (
        <div className="success-message">{successMessage}</div>
      )}

      {/* CSS dla animacji loading */}
      <style jsx>{`
        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </div>
  );
}

export default AddMaritexContact;
